import { useMemo, useState, useEffect, SetStateAction, useCallback, useRef, MutableRefObject } from 'react'
import { CSVLink } from 'react-csv'
import { process } from '@progress/kendo-data-query'

import { useFormatMessage } from 'lib/localization'
import { formatDate } from 'lib/datetime'
import config from 'config/appConfig'
import { ReportMessage, SearchFilter } from 'types/Messages'
import { BDSGridPagerConfig } from 'types/redux/dataTables/dataTables'
import { stopDeliverReasons } from 'config/filterMaps'

import { mapReasons, mapSize } from 'lib/mstore'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isPending, isSuccess } from 'redux/toolkit/api'
import {
  getRedeliveryQueueMessages,
  deleteMessage,
  postRecategorize,
  getSearch,
  checkAllSearchMessages,
  setRedeliveryQueueMessages,
  resetSearch,
  MlogViewConfig,
  postRedeliverMessage
} from 'redux/features/mstore/mstoreSlice'
import { setErrorSnackBar } from 'redux/features/app/appSlice'
import {
  update as updateMessagesTable,
  reset as resetMessagesTable
} from 'redux/features/dataTables/messages/messagesSlice'

export interface UseMessageLogListToolbarLogic {
  isGetSearchLoading: boolean
  disableRefresh: boolean
  badgeCount: number
  redeliveryOpen: boolean
  setRedeliveryOpen: (isOpen: boolean) => void
  recategorizeOtherOpen: boolean
  setRecategorizeOtherOpen: (isOpen: boolean) => void
  handleIfCanDeliver: () => void
  handleDelete: () => void
  anchorEl: HTMLElement | null
  setAnchorEl: (e: SetStateAction<HTMLElement | null>) => void
  handleRecategorizeClick: (category: string) => void
  handleSetOpenCategorizeOther: () => void
  eibEnabled: boolean
  setComposerOpen: (isOpen: boolean) => void
  handleOpenRedeliveryQueueModal: (status: boolean) => void
  pageConfig: BDSGridPagerConfig
  composerOpen: boolean
  redeliveryQueueOpen: boolean
  setRedeliveryQueueOpen: (isOpen: boolean) => void
  messages: ReportMessage[]
  onRefresh: () => void
  checkConfig: {
    isChecked: boolean
    isIndeterminate: boolean
    onCheckAll: () => void
  }
  exportConfig: {
    exportRef: MutableRefObject<(CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }) | null>
    fileName: () => string
    headers: {
      label: string
      key: string
    }[]
    csvExport: () => void
  }
  checkedMessages: ReportMessage[]
  searchFilter: SearchFilter
  viewConfig: MlogViewConfig
  handleDirectDeliver: () => void
  handleReject: () => void
}

const BASE_I18N_KEY = 'ess.message_log'

export const useMessageLogListToolbarLogic = (): UseMessageLogListToolbarLogic => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)

  const {
    accountPermissions,
    redeliveryQueue,
    isGetSearchLoading,
    isMessageHistoryOpen,
    mstoreSearch,
    checkedMessages,
    messageTable,
    searchFilter,
    viewConfig,
    isDeleteMessageSuccess
  } = useAppSelector(_store => ({
    accountPermissions: _store.settings.accountPermissions,
    redeliveryQueue: _store.mstore.redeliveryQueue,
    isGetSearchLoading: isPending(_store.mstore.getSearchApiStatus),
    isMessageHistoryOpen: _store.mstore.isMessageHistoryOpen,
    mstoreSearch: _store.mstore.search,
    checkedMessages: _store.mstore.checkedMessages,
    messageTable: _store.dataTables.messages,
    searchFilter: _store.mstore.searchFilter,
    viewConfig: _store.mstore.viewConfig,
    isDeleteMessageSuccess: isSuccess(_store.mstore.deleteMessageApiStatus)
  }))
  const dispatch = useAppDispatch()

  const exportRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)
  const [redeliveryOpen, setRedeliveryOpen] = useState(false)
  const [recategorizeOtherOpen, setRecategorizeOtherOpen] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [redeliveryQueueOpen, setRedeliveryQueueOpen] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [isIndeterminate, setIsIndeterminate] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const messagesRange = useMemo(
    () => ({
      from: messageTable.skip,
      to: Math.min(mstoreSearch?.messages?.length || 0, messageTable.skip + messageTable.take)
    }),
    [messageTable, mstoreSearch]
  )

  useEffect(() => {
    if (!mstoreSearch?.messages?.length || !checkedMessages.length) {
      setIsChecked(false)
      setIsIndeterminate(false)
      return
    }

    if (messagesRange.to - messagesRange.from === checkedMessages.length) {
      setIsChecked(true)
      setIsIndeterminate(false)
    } else {
      setIsChecked(false)
      setIsIndeterminate(true)
    }
  }, [checkedMessages, mstoreSearch, messagesRange])

  const formattedMessages = useMemo(
    () =>
      process(
        (checkedMessages || []).map((report: ReportMessage) => ({
          ...(report && {
            ...report,
            exportDate: formatDate(new Date(report.date), config.DATETIME.EXPORT_DATE_FORMAT),
            formattedSize: mapSize(report.size),
            reason: mapReasons(report.reasons, formatMessage)
          })
        })),
        {}
      ).data,
    [checkedMessages, formatMessage]
  )

  const handleOpenRedeliveryQueueModal = useCallback(
    (status: boolean) => {
      dispatch(getRedeliveryQueueMessages())
      setRedeliveryQueueOpen(status)
    },
    [dispatch]
  )

  const handleRecategorizeClick = useCallback(
    (category: string) => {
      dispatch(
        postRecategorize({
          messageIds: checkedMessages.map(message => message.mid),
          category
        })
      )

      setAnchorEl(null)
      setRecategorizeOtherOpen(false)
    },
    [dispatch, checkedMessages]
  )

  const handleSetOpenCategorizeOther = useCallback(() => {
    setAnchorEl(null)
    setRecategorizeOtherOpen(true)
  }, [])

  const onRefresh = useCallback(() => {
    dispatch(resetSearch({ dateReset: true }))
    dispatch(resetMessagesTable())
    dispatch(getSearch())
  }, [dispatch])

  const handleDelete = useCallback(() => {
    dispatch(
      deleteMessage({
        mids: checkedMessages.map(message => message.mid)
      })
    )
  }, [dispatch, checkedMessages])

  const handleIfCanDeliver = useCallback(() => {
    const reasonFound = checkedMessages.find(
      aMessage => aMessage.reasons.length && stopDeliverReasons.indexOf(aMessage.reasons[0]) > -1
    )
    if (reasonFound) {
      dispatch(
        setErrorSnackBar({
          message: 'stop_delivery_reasons',
          params: [formatMessage(`reason.${reasonFound.reasons[0]}`)]
        })
      )
    } else {
      dispatch(setRedeliveryQueueMessages(checkedMessages))
      setRedeliveryOpen(true)
    }
  }, [dispatch, formatMessage, checkedMessages])

  const handleDirectDeliver = useCallback(() => {
    dispatch(postRedeliverMessage(checkedMessages.map(msg => ({ messageId: msg.mid }))))
  }, [dispatch, checkedMessages])

  const handleReject = useCallback(() => {
    // TODO: Implement rejecting messages [BNESS-22803]
  }, [])

  const onCheckAll = useCallback(() => {
    dispatch(checkAllSearchMessages({ ...messagesRange, isChecked: !isChecked }))
  }, [dispatch, isChecked, messagesRange])

  const pageConfig: BDSGridPagerConfig = useMemo(() => {
    const { skip, take }: { skip: number; take: number } = messageTable

    return {
      pageable: {
        buttonCount: 0
      },
      skip,
      take,
      total: mstoreSearch?.messageCount || 0,
      onPageChange: (e: any) => {
        dispatch(updateMessagesTable({ skip: e.skip }))
        dispatch(checkAllSearchMessages({ ...messagesRange, isChecked: false }))
        setIsChecked(false)
        setIsIndeterminate(false)
        dispatch(getSearch())
      }
    }
  }, [messageTable, mstoreSearch, dispatch, messagesRange])

  const exportHeaders = useMemo(
    () => [
      { label: formatMessage('export.message_id'), key: 'mid' },
      { label: formatMessage('export.from'), key: 'envelopeFrom' },
      { label: formatMessage('export.to'), key: 'to' },
      { label: formatMessage('export.subject'), key: 'subject' },
      { label: formatMessage('export.date'), key: 'exportDate' },
      { label: formatMessage('export.size'), key: 'formattedSize' },
      { label: formatMessage('export.action'), key: 'status' },
      { label: formatMessage('export.reason'), key: 'reason' }
    ],
    [formatMessage]
  )

  const exportFileName = useCallback(() => {
    const currentDate = formatDate(new Date(), config.DATETIME.EXPORT_FILE_NAME_DATE_FORMAT)
    return `message_log_${currentDate}.csv`
  }, [])

  const csvExport = () => {
    if (exportRef?.current) {
      exportRef.current.link?.click()
    }
  }

  const disableRefresh = useMemo(
    () => isMessageHistoryOpen || isGetSearchLoading,
    [isMessageHistoryOpen, isGetSearchLoading]
  )

  useEffect(() => {
    if (isDeleteMessageSuccess) {
      onRefresh()
    }
  }, [isDeleteMessageSuccess, onRefresh])

  return useMemo(
    () => ({
      isGetSearchLoading,
      disableRefresh,
      badgeCount: redeliveryQueue?.results?.length || 0,
      redeliveryOpen,
      setRedeliveryOpen,
      recategorizeOtherOpen,
      setRecategorizeOtherOpen,
      handleIfCanDeliver,
      handleDelete,
      anchorEl,
      setAnchorEl,
      handleRecategorizeClick,
      handleSetOpenCategorizeOther,
      eibEnabled: accountPermissions?.emergencyInboxEnabled === 1,
      setComposerOpen,
      handleOpenRedeliveryQueueModal,
      pageConfig,
      composerOpen,
      redeliveryQueueOpen,
      setRedeliveryQueueOpen,
      messages: mstoreSearch.messages,
      onRefresh,
      checkConfig: {
        isChecked,
        isIndeterminate,
        onCheckAll
      },
      exportConfig: {
        exportRef,
        fileName: exportFileName,
        headers: exportHeaders,
        csvExport
      },
      checkedMessages: formattedMessages,
      searchFilter,
      viewConfig,
      handleDirectDeliver,
      handleReject
    }),
    [
      disableRefresh,
      redeliveryQueue,
      isGetSearchLoading,
      redeliveryOpen,
      setRedeliveryOpen,
      recategorizeOtherOpen,
      setRecategorizeOtherOpen,
      handleIfCanDeliver,
      handleDelete,
      anchorEl,
      setAnchorEl,
      handleRecategorizeClick,
      handleSetOpenCategorizeOther,
      accountPermissions,
      handleOpenRedeliveryQueueModal,
      composerOpen,
      redeliveryQueueOpen,
      setRedeliveryQueueOpen,
      isChecked,
      isIndeterminate,
      mstoreSearch,
      onCheckAll,
      onRefresh,
      pageConfig,
      exportFileName,
      exportHeaders,
      searchFilter,
      viewConfig,
      handleDirectDeliver,
      handleReject,
      formattedMessages
    ]
  )
}
